import { BASE_URL, CONTACT_INFO, SOCIAL_LINKS } from "@/lib/constants";

interface ArticleSchemaProps {
	title: string;
	description: string;
	url: string;
	image: string;
	datePublished: string;
	dateModified: string;
	authorName?: string;
}

interface OrganizationSchemaProps {
	name?: string;
	url?: string;
	logo?: string;
}

interface ServiceSchemaProps {
	name: string;
	url: string;
	description: string;
}

interface FAQSchemaProps {
	question: string;
	answer: string;
}

export const generateArticleSchema = (props: ArticleSchemaProps) => ({
	"@context": "https://schema.org",
	"@type": "Article",
	headline: props.title,
	description: props.description,
	image: props.image,
	datePublished: props.datePublished,
	dateModified: props.dateModified,
	author: {
		"@type": "Person",
		name: props.authorName || "Craftlayer Team",
	},
	publisher: {
		"@type": "Organization",
		name: "Craftlayer",
		logo: {
			"@type": "ImageObject",
			url: `${BASE_URL}/logo.png`,
		},
	},
	mainEntityOfPage: {
		"@type": "WebPage",
		"@id": props.url,
	},
});

export const generateOrganizationSchema = (
	props: OrganizationSchemaProps = {},
) => ({
	"@context": "https://schema.org",
	"@type": "Organization",
	name: props.name || "Craftlayer",
	url: props.url || BASE_URL,
	logo: props.logo || `${BASE_URL}/logo.png`,
	sameAs: [
		SOCIAL_LINKS.instagram,
		SOCIAL_LINKS.linkedin,
		SOCIAL_LINKS.twitter,
		SOCIAL_LINKS.facebook,
		SOCIAL_LINKS.github,
	],
});

export const generateWebsiteSchema = () => ({
	"@context": "https://schema.org",
	"@type": "WebSite",
	name: "Craftlayer",
	url: BASE_URL,
	description:
		"Craftlayer helps you build high-quality software with expert developers and modern technologies.",
	publisher: {
		"@type": "Organization",
		name: "Craftlayer",
	},
});

export const generateLocalBusinessSchema = () => ({
	"@context": "https://schema.org",
	"@type": "LocalBusiness",
	name: "Craftlayer",
	image: `${BASE_URL}/og-image.jpg`,
	"@id": BASE_URL,
	url: BASE_URL,
	telephone: CONTACT_INFO.telephone,
	email: CONTACT_INFO.email,
	address: {
		"@type": "PostalAddress",
		addressLocality: CONTACT_INFO.address.locality,
		addressCountry: CONTACT_INFO.address.country,
	},
	openingHoursSpecification: {
		"@type": "OpeningHoursSpecification",
		dayOfWeek: [
			"Monday",
			"Tuesday",
			"Wednesday",
			"Thursday",
			"Friday",
			"Saturday",
		],
		opens: "10:00",
		closes: "19:00",
	},
	priceRange: "$$",
});

export const generateServiceSchema = (services: ServiceSchemaProps[]) => ({
	"@context": "https://schema.org",
	"@type": "ItemList",
	itemListElement: services.map((service, index) => ({
		"@type": "ListItem",
		position: index + 1,
		item: {
			"@type": "Service",
			name: service.name,
			description: service.description,
			url: `${BASE_URL}${service.url}`,
		},
	})),
});

export const generateFAQSchema = (faqs: FAQSchemaProps[]) => ({
	"@context": "https://schema.org",
	"@type": "FAQPage",
	mainEntity: faqs.map((faq) => ({
		"@type": "Question",
		name: faq.question,
		acceptedAnswer: {
			"@type": "Answer",
			text: faq.answer,
		},
	})),
});
